
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ProductCard from '@/components/product/card/ProductCard';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/ui/container';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Star } from 'lucide-react';

// Define a more specific type for the fetched listing data
interface ProfileData {
  username: string | null;
  profile_image: string | null;
}

interface CategoryData {
  name: string;
}

// Define a type for potential error responses from Supabase
interface SelectQueryError<T extends string> {
  error: true;
  message: T;
}

interface FetchedListingBase {
  id: number;
  title: string;
  price: number;
  location: string;
  images: string[] | null;
  is_premium: boolean;
  created_at: string;
  condition: string | null;
  delivery_option: string | null;
  user_id: string | null;
  stock: number | null;
  is_price_negotiable: boolean;
  make: string | null;
  model: string | null;
  year: number | null;
  mileage: number | null;
  transmission: string | null;
  categories: CategoryData | null;
  subcategories: CategoryData | null;
  // Removed profiles from base, will be added separately
}

interface FetchedPremiumListing extends FetchedListingBase {
  profiles: ProfileData | null; // Profiles will be added after initial fetch
}

const fetchPremiumListings = async (): Promise<FetchedPremiumListing[]> => {
  // Step 1: Fetch listings without profile join
  const { data: listingsData, error: listingsError } = await supabase
    .from('listings')
    .select(`
      id, title, price, location, images, is_premium, created_at, condition,
      delivery_option, user_id, stock, is_price_negotiable, make, model, year,
      mileage, transmission,
      categories (name),
      subcategories (name)
    `)
    .eq('is_premium', true)
    .eq('is_active', true)
    .eq('approval_status', 'approved')
    .order('created_at', { ascending: false })
    .limit(8);

  if (listingsError) {
    console.error('Error fetching premium listings (base):', listingsError);
    throw new Error('Failed to fetch premium listings');
  }

  if (!listingsData) {
    return [];
  }

  const listingsBase = listingsData as FetchedListingBase[];

  // Step 2: Fetch profiles for these listings
  const userIds = listingsBase
    .map(listing => listing.user_id)
    .filter((id): id is string => id !== null); // Filter out null user_ids and type guard

  let profilesMap: Map<string, ProfileData> = new Map();

  if (userIds.length > 0) {
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, profile_image') // Corrected: select 'id'
      .in('id', userIds); // Corrected: filter by 'id'

    if (profilesError) {
      console.error('Error fetching profiles for premium listings:', profilesError);
      // Continue without profile data if profiles fetch fails
    } else if (profilesData && Array.isArray(profilesData)) { // Added Array.isArray check
      profilesData.forEach(profile => {
        if (profile.id) { // Corrected: use profile.id
          profilesMap.set(profile.id, { // Corrected: use profile.id as key
            username: profile.username || null,
            profile_image: profile.profile_image || null,
          });
        }
      });
    }
  }

  // Step 3: Combine listings with their profiles
  const typedData = listingsBase.map(listing => {
    const profile = listing.user_id ? profilesMap.get(listing.user_id) : null;
    return {
      ...listing,
      profiles: profile || null,
    } as FetchedPremiumListing;
  });
  
  return typedData;
};

const PremiumListings: React.FC = () => {
  const navigate = useNavigate();
  const { data: listings, isLoading, error } = useQuery<FetchedPremiumListing[], Error>({
    queryKey: ['premiumListings'],
    queryFn: fetchPremiumListings,
  });

  if (isLoading) {
    return (
      <Container className="py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center">
            <Star className="w-6 h-6 mr-2 text-yellow-400" /> Shpallje Premium
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-48 w-full rounded-md" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-8">
         <Alert variant="destructive">
          <AlertTitle>Gabim</AlertTitle>
          <AlertDescription>
            Ndodhi një problem gjatë ngarkimit të shpalljeve premium. Ju lutem provoni përsëri më vonë.
          </AlertDescription>
        </Alert>
      </Container>
    );
  }

  if (!listings || listings.length === 0) {
    return null; // Don't render the section if there are no premium listings
  }

  return (
    <Container className="py-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold flex items-center">
          <Star className="w-6 h-6 mr-2 text-yellow-400 fill-yellow-400" /> Shpallje Premium
        </h2>
        <Button variant="outline" onClick={() => navigate('/search?premium=true')}>
          Shiko të gjitha
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {listings.map((listing) => (
          <ProductCard
            key={listing.id}
            id={listing.id.toString()}
            title={listing.title}
            price={listing.price}
            location={listing.location}
            imageUrl={listing.images?.[0] || ''}
            category={listing.categories?.name || ''}
            subcategory={listing.subcategories?.name || ''}
            isPremium={listing.is_premium}
            createdAt={listing.created_at}
            condition={listing.condition || undefined} 
            deliveryOption={listing.delivery_option || undefined} 
            user_id={listing.user_id || undefined}
            stock={listing.stock === null ? undefined : listing.stock} 
            is_price_negotiable={listing.is_price_negotiable}
            userProfileImage={listing.profiles?.profile_image} 
            username={listing.profiles?.username}
            make={listing.make || undefined}
            model={listing.model || undefined}
            year={listing.year === null ? undefined : listing.year}
            mileage={listing.mileage === null ? undefined : listing.mileage}
            transmission={listing.transmission || undefined}
          />
        ))}
      </div>
    </Container>
  );
};

export default PremiumListings;
